import { makeAutoObservable, runInAction, reaction } from 'mobx';
import type RootStore from './RootStore';
import { Service } from '@feathersjs/feathers';
import EventEmitter from 'events';

export interface Collaborator {
  id: string;
  userId: string;
  userName: string;
  email: string;
  color: string;
  cursor?: {
    x: number;
    y: number;
    pageNumber: number;
  };
  isActive: boolean;
  joinedAt: Date;
}

export interface AnnotationUpdate {
  annotation: any;
  userId: string;
  timestamp: Date;
  operation: 'create' | 'update' | 'delete';
}

interface PdfCollaborationData {
  documentId?: string;
  userId?: string;
  userName?: string;
  email?: string;
  color?: string;
  annotation?: any;
  annotationId?: string;
  annotations?: any[];
  position?: { x: number; y: number; pageNumber: number };
  collaborators?: any[];
  etag?: string;
  joinedAt?: string;
  isActive?: boolean;
  timestamp?: string;
}

class PdfEditorStore {
  // Observables
  documentId: string | null = null;
  collaborators: Map<string, Collaborator> = new Map();
  isDocumentJoined = false;
  
  // Private properties
  rootStore: RootStore;
  private pdfCollaborationService: (Service<any> & EventEmitter) | null = null;
  private connectionStatusDispose: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false
    });
    
    // Initialize PDF collaboration service
    this.initializePdfService();
    
    // React to connection status changes
    this.setupConnectionReaction();
  }
  
  /**
   * Initialize PDF collaboration service from FeathersJS app
   */
  private initializePdfService() {
    if (this.rootStore.mode === 'test') return;
    
    try {
      // Get the PDF collaboration service from FeathersJS app
      this.pdfCollaborationService = this.rootStore.app.service('/pdf-collaboration');
      
      if (this.pdfCollaborationService) {
        this.setupServiceListeners();
      } else {
        console.warn('[PdfEditorStore] PDF collaboration service not available');
      }
    } catch (error) {
      console.warn('[PdfEditorStore] Failed to initialize PDF collaboration service:', error);
    }
  }
  
  /**
   * Setup connection status reaction
   */
  private setupConnectionReaction() {
    // React to connection status changes from ConnectionStore
    this.connectionStatusDispose = reaction(
      () => this.rootStore.connectionStore?.serverConnectionStatus,
      (isConnected) => {
        if (isConnected && this.documentId && !this.isDocumentJoined) {
          // Rejoin document when reconnected
          this.joinDocument(this.documentId);
        } else if (!isConnected) {
          runInAction(() => {
            this.isDocumentJoined = false;
          });
        }
      }
    );
  }
  
  /**
   * Setup FeathersJS service event listeners for PDF collaboration
   */
  private setupServiceListeners() {
    if (!this.pdfCollaborationService) return;
    
    // Document collaboration events
    this.pdfCollaborationService.on('joined', this.handleDocumentJoined.bind(this));
    this.pdfCollaborationService.on('userJoined', this.handleUserJoined.bind(this));
    this.pdfCollaborationService.on('userLeft', this.handleUserLeft.bind(this));
    this.pdfCollaborationService.on('collaboratorsList', this.handleCollaboratorsList.bind(this));
    
    // Annotation events
    this.pdfCollaborationService.on('annotationCreated', this.handleAnnotationCreated.bind(this));
    this.pdfCollaborationService.on('annotationUpdated', this.handleAnnotationUpdated.bind(this));
    this.pdfCollaborationService.on('annotationDeleted', this.handleAnnotationDeleted.bind(this));
    this.pdfCollaborationService.on('annotationsBatch', this.handleAnnotationsBatch.bind(this));
    
    // Cursor events
    this.pdfCollaborationService.on('cursorMove', this.handleCursorMove.bind(this));
  }
  
  /**
   * Join a document channel for collaboration
   */
  async joinDocument(documentId: string) {
    if (!this.pdfCollaborationService) {
      console.warn('[PdfEditorStore] PDF collaboration service not available');
      return;
    }
    
    if (!this.rootStore.connectionStore?.serverConnectionStatus) {
      console.warn('[PdfEditorStore] Not connected to server');
      return;
    }
    
    // Leave previous document if any
    if (this.documentId && this.isDocumentJoined) {
      await this.leaveDocument();
    }
    
    try {
      // Use FeathersJS service to join document
      const response = await this.pdfCollaborationService.create({
        action: 'join',
        documentId
      });
      
      runInAction(() => {
        this.documentId = documentId;
        this.isDocumentJoined = true;
      });
      
      console.log(`[PdfEditorStore] Joining document: ${documentId}`);
      return response;
    } catch (error) {
      console.error('[PdfEditorStore] Failed to join document:', error);
      runInAction(() => {
        this.isDocumentJoined = false;
      });
    }
  }
  
  /**
   * Leave current document channel
   */
  async leaveDocument() {
    if (!this.pdfCollaborationService || !this.documentId) return;
    
    try {
      await this.pdfCollaborationService.create({
        action: 'leave',
        documentId: this.documentId
      });
      
      runInAction(() => {
        this.documentId = null;
        this.isDocumentJoined = false;
        this.collaborators.clear();
      });
      
      console.log(`[PdfEditorStore] Left document: ${this.documentId}`);
    } catch (error) {
      console.error('[PdfEditorStore] Failed to leave document:', error);
    }
  }
  
  /**
   * Send annotation update
   */
  async sendAnnotationUpdate(annotation: any, operation: 'create' | 'update' | 'delete') {
    if (!this.pdfCollaborationService || !this.isDocumentJoined || !this.documentId) {
      console.warn('[PdfEditorStore] Cannot send annotation update - not joined to document');
      return;
    }
    
    try {
      await this.pdfCollaborationService.create({
        action: 'annotationUpdate',
        documentId: this.documentId,
        annotation,
        operation,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('[PdfEditorStore] Failed to send annotation update:', error);
    }
  }
  
  /**
   * Send batch annotation updates
   */
  async sendAnnotationsBatch(annotations: any[], etag?: string) {
    if (!this.pdfCollaborationService || !this.isDocumentJoined || !this.documentId) {
      console.warn('[PdfEditorStore] Cannot send annotations batch - not joined to document');
      return;
    }
    
    try {
      await this.pdfCollaborationService.create({
        action: 'annotationsBatch',
        documentId: this.documentId,
        annotations,
        etag,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('[PdfEditorStore] Failed to send annotations batch:', error);
    }
  }
  
  /**
   * Send cursor position
   */
  async sendCursorPosition(x: number, y: number, pageNumber: number) {
    if (!this.pdfCollaborationService || !this.isDocumentJoined || !this.documentId) {
      // Silently fail for cursor updates as they're not critical
      return;
    }
    
    try {
      await this.pdfCollaborationService.create({
        action: 'cursorMove',
        documentId: this.documentId,
        position: { x, y, pageNumber },
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      // Silently fail for cursor updates as they're not critical
    }
  }
  
  /**
   * Handle document joined confirmation
   */
  private handleDocumentJoined(data: PdfCollaborationData) {
    runInAction(() => {
      this.isDocumentJoined = true;
      if (data.documentId) {
        this.documentId = data.documentId;
      }
    });
    console.log(`[PdfEditorStore] Successfully joined document: ${data.documentId}`);
    this.onDocumentJoined?.(data);
  }
  
  /**
   * Handle user joined event
   */
  private handleUserJoined(data: PdfCollaborationData) {
    if (!data.userId) return;
    
    runInAction(() => {
      const collaborator: Collaborator = {
        id: data.userId,
        userId: data.userId,
        userName: data.userName || 'Anonymous',
        email: data.email || '',
        color: data.color || this.generateUserColor(data.userId),
        isActive: true,
        joinedAt: new Date(data.joinedAt || Date.now()),
      };
      this.collaborators.set(data.userId, collaborator);
    });
    
    console.log(`[PdfEditorStore] User joined: ${data.userName || data.userId}`);
    this.onUserJoined?.(data);
  }
  
  /**
   * Handle user left event
   */
  private handleUserLeft(data: PdfCollaborationData) {
    if (!data.userId) return;
    
    runInAction(() => {
      this.collaborators.delete(data.userId);
    });
    
    console.log(`[PdfEditorStore] User left: ${data.userName || data.userId}`);
    this.onUserLeft?.(data);
  }
  
  /**
   * Handle collaborators list
   */
  private handleCollaboratorsList(data: PdfCollaborationData) {
    runInAction(() => {
      this.collaborators.clear();
      data.collaborators?.forEach((collab: any) => {
        const collaborator: Collaborator = {
          id: collab.userId,
          userId: collab.userId,
          userName: collab.userName || 'Anonymous',
          email: collab.email || '',
          color: collab.color || this.generateUserColor(collab.userId),
          isActive: collab.isActive !== false,
          joinedAt: new Date(collab.joinedAt || Date.now()),
        };
        this.collaborators.set(collab.userId, collaborator);
      });
    });
    
    console.log(`[PdfEditorStore] Received ${data.collaborators?.length || 0} collaborators`);
  }
  
  /**
   * Handle annotation created event
   */
  private handleAnnotationCreated(data: PdfCollaborationData) {
    console.log('[PdfEditorStore] Annotation created:', data);
    if (data.annotation && data.userId) {
      this.onAnnotationCreated?.(data.annotation, data.userId);
    }
  }
  
  /**
   * Handle annotation updated event
   */
  private handleAnnotationUpdated(data: PdfCollaborationData) {
    console.log('[PdfEditorStore] Annotation updated:', data);
    if (data.annotation && data.userId) {
      this.onAnnotationUpdated?.(data.annotation, data.userId);
    }
  }
  
  /**
   * Handle annotation deleted event
   */
  private handleAnnotationDeleted(data: PdfCollaborationData) {
    console.log('[PdfEditorStore] Annotation deleted:', data);
    if (data.annotationId && data.userId) {
      this.onAnnotationDeleted?.(data.annotationId, data.userId);
    }
  }
  
  /**
   * Handle batch annotations update
   */
  private handleAnnotationsBatch(data: PdfCollaborationData) {
    console.log('[PdfEditorStore] Batch annotations received:', data);
    if (data.annotations) {
      this.onAnnotationsBatch?.(data.annotations, data.etag);
    }
  }
  
  /**
   * Handle cursor move event
   */
  private handleCursorMove(data: PdfCollaborationData) {
    if (!data.userId || !data.position) return;
    
    runInAction(() => {
      const collaborator = this.collaborators.get(data.userId);
      if (collaborator) {
        collaborator.cursor = data.position;
      }
    });
    
    this.onCursorMove?.(data.userId, data.position);
  }
  
  /**
   * Generate a color for a user based on their ID
   */
  private generateUserColor(userId: string): string {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
      '#FFEAA7', '#DDA0DD', '#98D8C8', '#FFB6C1',
      '#87CEEB', '#F0E68C', '#D3D3D3', '#FFE4B5',
    ];
    
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  }
  
  /**
   * Event callbacks (to be overridden or assigned by components)
   */
  onDocumentJoined?: (data: PdfCollaborationData) => void;
  onUserJoined?: (data: PdfCollaborationData) => void;
  onUserLeft?: (data: PdfCollaborationData) => void;
  onAnnotationCreated?: (annotation: any, userId: string) => void;
  onAnnotationUpdated?: (annotation: any, userId: string) => void;
  onAnnotationDeleted?: (annotationId: string, userId: string) => void;
  onAnnotationsBatch?: (annotations: any[], etag?: string) => void;
  onCursorMove?: (userId: string, position: { x: number; y: number; pageNumber: number }) => void;
  
  /**
   * Cleanup and disconnect
   */
  dispose() {
    // Leave document if joined
    if (this.documentId) {
      this.leaveDocument();
    }
    
    // Remove all service event listeners
    if (this.pdfCollaborationService) {
      this.pdfCollaborationService.removeListener('joined', this.handleDocumentJoined);
      this.pdfCollaborationService.removeListener('userJoined', this.handleUserJoined);
      this.pdfCollaborationService.removeListener('userLeft', this.handleUserLeft);
      this.pdfCollaborationService.removeListener('collaboratorsList', this.handleCollaboratorsList);
      this.pdfCollaborationService.removeListener('annotationCreated', this.handleAnnotationCreated);
      this.pdfCollaborationService.removeListener('annotationUpdated', this.handleAnnotationUpdated);
      this.pdfCollaborationService.removeListener('annotationDeleted', this.handleAnnotationDeleted);
      this.pdfCollaborationService.removeListener('annotationsBatch', this.handleAnnotationsBatch);
      this.pdfCollaborationService.removeListener('cursorMove', this.handleCursorMove);
    }
    
    // Dispose of connection reaction
    if (this.connectionStatusDispose) {
      this.connectionStatusDispose();
      this.connectionStatusDispose = null;
    }
    
    runInAction(() => {
      this.isDocumentJoined = false;
      this.collaborators.clear();
    });
  }
  
  /**
   * Get connection status from ConnectionStore
   */
  get isConnected(): boolean {
    return this.rootStore.connectionStore?.serverConnectionStatus ?? false;
  }
  
  /**
   * Get active collaborators
   */
  get activeCollaborators(): Collaborator[] {
    return Array.from(this.collaborators.values()).filter(c => c.isActive);
  }
  
  /**
   * Get collaborator by ID
   */
  getCollaborator(userId: string): Collaborator | undefined {
    return this.collaborators.get(userId);
  }
}

export default PdfEditorStore;